import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { LEGAL_DOCUMENTS } from '../../content/legal';

interface ConsentModalProps {
  open: boolean;
  onAccepted: () => void;
}

const ConsentModal: React.FC<ConsentModalProps> = ({ open, onAccepted }) => {
  const { t } = useTranslation('modals');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!termsAccepted) {
      setError(t('consent.error'));
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const termsEntry = LEGAL_DOCUMENTS.find((doc) => doc.slug === 'terms-and-conditions');
      const privacyEntry = LEGAL_DOCUMENTS.find((doc) => doc.slug === 'privacy-policy');
      await axiosInstance.post(API_ENDPOINTS.auth.acceptTerms, {
        accept_terms: true,
        whatsapp_consent: whatsappConsent,
        terms_version: termsEntry?.version || '1.0',
        privacy_version: privacyEntry?.version || '1.0',
      });
      onAccepted();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
          px: 3,
          py: 3,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GavelRoundedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>
              {t('consent.title')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7, color: '#fff' }}>
              {t('consent.subtitle')}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {/* Terms & Conditions */}
        <Box
          sx={{
            p: 2.5,
            border: '1.5px solid',
            borderColor: termsAccepted ? 'primary.main' : 'divider',
            borderRadius: '14px',
            mb: 2,
            transition: 'border-color 0.2s',
            background: termsAccepted ? 'rgba(6, 182, 212, 0.04)' : 'transparent',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) setError('');
                }}
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': { color: 'primary.main' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                I accept the{' '}
                <Typography
                  component="a"
                  href="/terms"
                  target="_blank"
                  variant="body2"
                  sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}
                >
                  Terms of Use
                </Typography>{' '}
                and{' '}
                <Typography
                  component="a"
                  href="/privacy-policy"
                  target="_blank"
                  variant="body2"
                  sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}
                >
                  Privacy Policy
                </Typography>
              </Typography>
            }
            sx={{ m: 0, alignItems: 'flex-start' }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, ml: 4 }}>
            {t('consent.termsHelper')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75, ml: 4 }}>
            {t('consent.aiHelper')}{' '}
            <Typography
              component="a"
              href="/ai-health-terms"
              target="_blank"
              variant="caption"
              sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'none' }}
            >
              {t('consent.aiTermsLink')}
            </Typography>
          </Typography>
        </Box>

        {/* WhatsApp Consent */}
        <Box
          sx={{
            p: 2.5,
            border: '1.5px solid',
            borderColor: whatsappConsent ? '#25D366' : 'divider',
            borderRadius: '14px',
            transition: 'border-color 0.2s',
            background: whatsappConsent ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={whatsappConsent}
                onChange={(e) => setWhatsappConsent(e.target.checked)}
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': { color: '#25D366' },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WhatsAppIcon sx={{ color: '#25D366', fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t('consent.whatsappConsent')}
                </Typography>
              </Box>
            }
            sx={{ m: 0, alignItems: 'flex-start' }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, ml: 4 }}>
            {t('consent.whatsappHelper')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={submitting || !termsAccepted}
          sx={{
            borderRadius: '12px',
            py: 1.5,
            fontWeight: 700,
            fontSize: '0.95rem',
            background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0891b2, #7c3aed)',
            },
          }}
        >
          {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('consent.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConsentModal;
